import React from 'react';
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import classnames from 'classnames';
import { StandardRadioGroup } from '../StandardRadioGroup';
import styles from '../FilterSidebar.module.css';

/**
 * Универсальный контейнер для группирования нескольких зависимых фильтров
 * с поддержкой условного рендеринга групп и footer-а
 *
 * @param {Object} props
 * @param {boolean} props.isVisible - Условие видимости всего блока
 * @param {Array<Object>} props.groups - Массив групп фильтров
 *
 * Структура группы:
 * {
 *   id: string,                           // Уникальный ключ для React
 *   type?: 'radio-group' | 'button-group' // Тип контрола (по умолчанию 'radio-group')
 *   title?: string,                       // Заголовок группы (опционально)
 *   value: string|number,                 // Текущее значение
 *   onChange: function,                   // Обработчик изменения
 *   options: Array<{                      // Опции для RadioGroup/ButtonGroup
 *     value: string|number,
 *     label: React.ReactNode,
 *     disabled?: boolean,                 // Отключить эту опцию
 *   }>,
 *   renderLabel?: function(option),       // Функция для кастомизации рендера label
 *   when?: boolean | function()           // Условие показа группы (опционально)
 * }
 *
 * @param {React.ReactNode | function} props.footer - Дополнительный контент после всех групп (опционально)
 *                                                    Может быть функцией для условного рендеринга
 *
 * @example
 * // Простой пример с RadioGroups
 * <MultiGroupFilter
 *   isVisible={true}
 *   groups={[
 *     {
 *       id: 'period',
 *       title: 'Период:',
 *       value: String(days),
 *       onChange: onChangeFilter,
 *       options: filterValues.map(v => ({
 *         value: v,
 *         label: t`${v} дней`
 *       }))
 *     }
 *   ]}
 * />
 *
 * // Пример с ButtonGroup + RadioGroup
 * <MultiGroupFilter
 *   isVisible={true}
 *   groups={[
 *     {
 *       id: 'mode',
 *       type: 'button-group',
 *       value: mode,
 *       onChange: e => onChangeMode(e.target.value),
 *       options: ['ABS', 'SHARE'].map(m => ({
 *         value: m,
 *         label: m
 *       }))
 *     },
 *     {
 *       id: 'days',
 *       value: String(days),
 *       onChange: onChangeDays,
 *       options: [7, 14, 30].map(d => ({
 *         value: d,
 *         label: t`${d} дней`
 *       }))
 *     }
 *   ]}
 * />
 *
 * // Пример с условным рендерингом групп
 * <MultiGroupFilter
 *   isVisible={true}
 *   groups={[
 *     {
 *       id: 'mode',
 *       type: 'button-group',
 *       value: mode,
 *       onChange: e => onChangeMode(e.target.value),
 *       options: modeOptions
 *     },
 *     {
 *       id: 'competitors',
 *       value: competitor,
 *       onChange: onChangeCompetitor,
 *       options: competitorOptions,
 *       when: mode === 'starts' || mode === 'ms'  // Показать только если выбран определённый mode
 *     },
 *     {
 *       id: 'days',
 *       value: String(days),
 *       onChange: onChangeDays,
 *       options: dayOptions
 *     }
 *   ]}
 *   footer={legendContent}  // или функция для условного рендеринга
 * />
 */
const MultiGroupFilter = ({ isVisible, groups, footer }) => {
    if (!isVisible) {
        return null;
    }

    // Проверяем условие группы (может быть boolean или функция)
    const shouldShowGroup = group => {
        if (typeof group.when !== 'undefined' && group.when !== null) {
            if (typeof group.when === 'function') {
                return group.when();
            }
            return Boolean(group.when);
        }
        return true;
    };
    const renderGroup = group => {
        const isButtonGroup = group.type === 'button-group';

        return (
            <div key={group.id}>
                {group.title && <div className={styles.legHead}>{group.title}</div>}

                {isButtonGroup ? (
                    // Рендер ButtonGroup
                    <div className={styles.filterButtonGroupContainerMedium}>
                        <ButtonGroup
                            variant='contained'
                            size='small'
                            className={styles.filterButtonGroup}
                        >
                            {group.options.map(option => {
                                const label = group.renderLabel
                                    ? group.renderLabel(option)
                                    : option.label;

                                return (
                                    <Button
                                        key={option.value}
                                        onClick={() => group.onChange({
                                            target: { value: option.value },
                                        })}
                                        color={group.value === String(option.value) ? 'primary' : 'default'}
                                        disabled={option.disabled || false}
                                    >
                                        {label}
                                    </Button>
                                );
                            })}
                        </ButtonGroup>
                    </div>
                ) : (
                    // Рендер RadioGroup (по умолчанию)
                    <StandardRadioGroup
                        value={group.value}
                        onChange={group.onChange}
                    >
                        {group.options.map(option => {
                            const label = group.renderLabel
                                ? group.renderLabel(option)
                                : option.label;

                            return (
                                <FormControlLabel
                                    key={option.value}
                                    className={styles.isochroneFilterLabel}
                                    control={
                                        <Radio
                                            checked={group.value === String(option.value)}
                                            onChange={group.onChange}
                                            value={String(option.value)}
                                            className={classnames(
                                                styles.isochroneFilterInput, {
                                                    [styles.checkedIsochroneFilterInput]: group.value === String(option.value),
                                                }
                                            )}
                                            size='small'
                                            disabled={option.disabled || false}
                                        />
                                    }
                                    label={label}
                                    labelPlacement='end'
                                />
                            );
                        })}
                    </StandardRadioGroup>
                )}
            </div>
        );
    };
    const renderedFooter = typeof footer === 'function' ? footer() : footer;
    return (
        <>
            {groups.filter(shouldShowGroup).map(renderGroup)}
            {renderedFooter && <div style={{ marginTop: '8px' }}>{renderedFooter}</div>}
        </>
    );
};

MultiGroupFilter.propTypes = {
    isVisible: PropTypes.bool.isRequired,
    groups: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        type: PropTypes.oneOf([ 'radio-group', 'button-group' ]),
        title: PropTypes.node,
        value: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]).isRequired,
        onChange: PropTypes.func.isRequired,
        options: PropTypes.arrayOf(PropTypes.shape({
            value: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]).isRequired,
            label: PropTypes.node.isRequired,
            disabled: PropTypes.bool,
        })).isRequired,
        renderLabel: PropTypes.func,
        when: PropTypes.oneOfType([ PropTypes.bool, PropTypes.func ]),
    })).isRequired,
    footer: PropTypes.oneOfType([ PropTypes.node, PropTypes.func ]),
};

MultiGroupFilter.defaultProps = {
    footer: null,
};

export { MultiGroupFilter };
